<template>
    <div class="layout-px-spacing">
        <teleport to="#breadcrumb">
            <ul class="navbar-nav flex-row">
                <li>
                    <div class="page-header">
                        <nav class="breadcrumb-one" aria-label="breadcrumb">
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item active" aria-current="page"><span>View Actions Taken</span>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </li>
            </ul>
        </teleport>
        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h3 class="mb-0">
                                Here is the List of your available Actions to be Taken so far! <br>
                                <strong>
                                    You have {{ actionsTakenList.length }} registered Actions Taken.
                                </strong>
                            </h3>
                            <button v-if="hasCreatePermission" @click="showAddModal"
                                class="btn btn-primary d-flex align-items-center">
                                Add Actions Taken
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-plus ms-2">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>
                        </div>

                        <div v-if="loading">
                            <div class="spinner-grow text-info align-self-center loader-lg">Loading...</div>
                        </div>

                        <div v-else>
                            <div class="table-responsive">
                                <table class="table table-hover table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Date Created</th>
                                            <th class="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="actionTaken in actionsTakenList" :key="actionTaken.id">
                                            <td>
                                                {{ actionTaken.name }}
                                            </td>
                                            <td>{{ formatDate(actionTaken.created_at) }}</td>
                                            <td class="text-center">
                                                <!-- Edit Button -->
                                                <button v-if="hasEditPermission" @click="showEditModal(actionTaken)"
                                                    class="btn btn-primary btn-sm me-2 mb-2">
                                                    Edit
                                                </button>
                                                <button v-if="hasDeletePermission"
                                                    @click="deleteActionsTaken(actionTaken.id)"
                                                    class="btn btn-danger btn-sm mb-2">
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div> <!-- End of Table -->
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Modal -->
        <div class="modal fade" id="addActionsTakenModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Add New Action Taken</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="handleAddSubmit">
                            <div class="mb-3">
                                <label for="name" class="form-label">Name</label>
                                <input type="text" class="form-control" id="name" v-model="addForm.name" required>
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                                <button type="submit" class="btn btn-primary" :disabled="addLoading">
                                    <span v-if="addLoading" class="spinner-border spinner-border-sm"></span>
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Modal -->
        <div class="modal fade" id="editActionsTakenModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Edit Action Taken</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="handleEditSubmit">
                            <div class="mb-3">
                                <label for="editName" class="form-label">Name</label>
                                <input type="text" class="form-control" id="editName" v-model="editForm.name" required>
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                                <button type="submit" class="btn btn-primary" :disabled="editLoading">
                                    <span v-if="editLoading" class="spinner-border spinner-border-sm"></span>
                                    Update
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.layout-px-spacing {
    min-height: calc(100vh - 170px) !important;
}

.cursor-pointer {
    cursor: pointer;
}

h3 {
    font-size: 28px;
    font-weight: 700;
    color: #3c4d69;
}
</style>

<script setup>
import { useMeta } from "@/composables/use-meta";
import { ref, onMounted, computed } from "vue";
import { useStore } from 'vuex';
import axios from 'axios';
import { BASE_URL } from '@/Config/config';
import Swal from 'sweetalert2';
import { Modal } from 'bootstrap';

useMeta({ title: "View Actions Taken" });

const store = useStore();
const actionsTakenList = ref([]);
const loading = ref(false);
const addLoading = ref(false);
const editLoading = ref(false);

// Modal instances
let addModal = null;
let editModal = null;

// Form data
const addForm = ref({
    name: ''
});

const editForm = ref({
    id: null,
    name: ''
});

// Permission Checks
const hasViewPermission = computed(() => store.getters.hasPermission('view_taken_actions'));
const hasCreatePermission = computed(() => store.getters.hasPermission('create_taken_actions'));
const hasEditPermission = computed(() => store.getters.hasPermission('edit_taken_actions'));
const hasDeletePermission = computed(() => store.getters.hasPermission('delete_taken_actions'));

// Initialize modals after component mounts
onMounted(async () => {
    await loadActionsTaken();
    addModal = new Modal(document.getElementById('addActionsTakenModal'));
    editModal = new Modal(document.getElementById('editActionsTakenModal'));
});

// SweetAlert configuration
const showConfirmDialog = async (title, text) => {
    return await Swal.fire({
        title: title,
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Delete',
        padding: '2em'
    });
};

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6'
    });
};

const loadActionsTaken = async () => {
    try {
        loading.value = true;
        const response = await axios.get(`${BASE_URL}/taken-actions`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        actionsTakenList.value = response.data;
    } catch (error) {
        console.error('Error fetching actions taken:', error);
        showErrorAlert('Failed to load actions taken. Please try again later.');
    } finally {
        loading.value = false;
    }
}

// Show Add Modal
const showAddModal = () => {
    addForm.value = { name: '' };
    addModal.show();
};

// Handle Add Submit
const handleAddSubmit = async () => {
    try {
        addLoading.value = true;
        const response = await axios.post(`${BASE_URL}/taken-actions`, addForm.value, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        actionsTakenList.value.push(response.data);
        addModal.hide();
        showSuccessAlert('Action Taken added successfully!');
    } catch (error) {
        console.error('Error adding Action Taken:', error);
        showErrorAlert('Failed to add Action Taken. Please try again later.');
    } finally {
        addLoading.value = false;
    }
};

// Show Edit Modal
const showEditModal = (actionTaken) => {
    editForm.value = {
        id: actionTaken.id,
        name: actionTaken.name
    };
    editModal.show();
};

// Handle Edit Submit
const handleEditSubmit = async () => {
    try {
        editLoading.value = true;
        await axios.put(`${BASE_URL}/taken-actions/${editForm.value.id}`, editForm.value, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        const index = actionsTakenList.value.findIndex(item => item.id === editForm.value.id);
        if (index !== -1) {
            actionsTakenList.value[index].name = editForm.value.name;
        }

        editModal.hide();
        showSuccessAlert('Action Taken updated successfully!');
    } catch (error) {
        console.error('Error updating Action Taken:', error);
        showErrorAlert('Failed to update Action Taken. Please try again later.');
    } finally {
        editLoading.value = false;
    }
};

// Delete Actions Taken
const deleteActionsTaken = async (id) => {
    const result = await showConfirmDialog(
        'Are you sure?',
        'This action cannot be undone. Do you want to proceed?'
    );

    if (result.isConfirmed) {
        try {
            await axios.delete(`${BASE_URL}/taken-actions/${id}`, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });
            actionsTakenList.value = actionsTakenList.value.filter(v => v.id !== id);
            showSuccessAlert('Action Taken deleted successfully!');
        } catch (error) {
            console.error('Error deleting Action Taken:', error);
            showErrorAlert('Failed to delete Action Taken. Please try again later.');
        }
    }
};

// Format Date
const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
};
</script>